import { useEffect, useMemo, useRef, useState } from 'react';
import { GrDocumentDownload } from 'react-icons/gr';
import { MdDownload } from 'react-icons/md';
import { RxEyeOpen } from 'react-icons/rx';

import { getDocumentById } from '@/store/documents/documents.api.ts';
import type { SingleDocument } from '@/store/documents/documents.dto.ts';
import { downloadFromLink } from '@/utils/download-file-from-link.ts';
import DataTable, { type Column } from '@components/DataTable/DataTable.tsx';
import { DocumentPreviewModal } from '@components/DocumentPreview/DocumentPreview.tsx';

import styles from './ExpensesTable.module.scss';

type ExpensesTableProps<T extends { id: string | number }> = {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  getDocumentId?: (row: T) => string | undefined;
  getDocumentDisplayName?: (row: T) => string | undefined;
  autoFit?: boolean;
  renderSubRow?: (row: T) => React.ReactNode;
  rowClassName?: (row: T) => string | undefined;
};

export const ExpensesTable = <T extends { id: string | number }>({
  data,
  columns,
  onRowClick,
  getDocumentId,
  getDocumentDisplayName,
  autoFit = true,
  renderSubRow,
  rowClassName,
}: ExpensesTableProps<T>) => {
  const [documentsById, setDocumentsById] = useState<Record<string, SingleDocument>>({});
  const [previewDocument, setPreviewDocument] = useState<SingleDocument | null>(null);
  const requestedIdsRef = useRef<Set<string>>(new Set());

  const documentIds = useMemo(() => {
    if (!getDocumentId) return [];
    return Array.from(
      new Set(data.map(getDocumentId).filter((docId): docId is string => Boolean(docId))),
    );
  }, [data, getDocumentId]);

  useEffect(() => {
    const missingIds = documentIds.filter((id) => !requestedIdsRef.current.has(id));
    if (!missingIds.length) return;
    missingIds.forEach((id) => requestedIdsRef.current.add(id));

    const loadDocuments = async () => {
      const results = await Promise.allSettled(missingIds.map((id) => getDocumentById(id)));
      const nextDocuments: Record<string, SingleDocument> = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          nextDocuments[result.value.id] = result.value;
        }
      });
      if (Object.keys(nextDocuments).length) {
        setDocumentsById((prev) => ({ ...prev, ...nextDocuments }));
      }
    };

    void loadDocuments();
  }, [documentIds]);

  const columnsWithDocuments = useMemo(() => {
    if (!getDocumentId) return columns;

    return columns.map((col) => {
      if (col.key === 'docs' || col.key === 'documentId') {
        return {
          ...col,
          render: (_value: unknown, row: T) => {
            const docId = getDocumentId(row);
            const displayName = getDocumentDisplayName?.(row);
            if (!docId && !displayName) {
              return <span className={styles.noDocument}>No document attached</span>;
            }
            const document = docId ? documentsById[docId] : undefined;
            return (
              <div className={styles.document}>
                <GrDocumentDownload width={16} height={20} color={'#38BDF8'} />

                {(document || displayName) && (
                  <div className={styles.documentInfo}>
                    <span className={styles.documentName}>{displayName || document?.name}</span>
                    {document && (
                      <span className={styles.documentMeta}>
                        {(document.size / 1024).toFixed(0)}kb
                      </span>
                    )}
                  </div>
                )}
                {document && (
                  <>
                    <button
                      className={styles.actionButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (document?.storageLink) {
                          void downloadFromLink(document.storageLink, document.name);
                        }
                      }}
                    >
                      <MdDownload />
                    </button>
                    <button
                      className={styles.actionButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPreviewDocument(document);
                      }}
                    >
                      <RxEyeOpen />
                    </button>
                  </>
                )}
              </div>
            );
          },
        };
      }
      return col;
    });
  }, [columns, documentsById, getDocumentId, getDocumentDisplayName]);

  return (
    <>
      <DataTable
        data={data}
        columns={columnsWithDocuments}
        onRowClick={onRowClick}
        autoFit={autoFit}
        renderSubRow={renderSubRow}
        rowClassName={rowClassName}
      />
      {previewDocument && (
        <DocumentPreviewModal document={previewDocument} onClose={() => setPreviewDocument(null)} />
      )}
    </>
  );
};

export { type Column };
export default ExpensesTable;
